import { wrapInList } from 'prosemirror-schema-list';
import { toggleMark } from 'prosemirror-commands';
import { Dropdown, MenuItem } from 'prosemirror-menu';
import { undo, redo } from 'prosemirror-history';
import { openPrompt } from '../prompt';
import { TextField } from '../TextField';
import {
  alignmentItem,
  applyMark,
  blockTypeIsActive,
  cmdItem,
  markItem,
  toggleBlockType,
} from './common';
import icons, { createIcon } from '../icons';
import { markActive } from '../utils';
import { getFixedDialogStyle } from 'dashboard/helper/dialogPositionHelper';

const ALIGNMENTS = ['left', 'center', 'right', 'justify'];

const ALIGNMENT_CLASS_NAMES = {
  left: 'menu-item-alignLeft-option',
  center: 'menu-item-alignCenter-option',
  right: 'menu-item-alignRight-option',
  justify: 'menu-item-alignJustify-option',
};

const TEXT_COLORS = [
  '#26292b',
  '#FF0000',
  '#00FF00',
  '#0000FF',
  '#FFFF00',
  '#FF00FF',
  '#00FFFF',
  '#808080',
  '#800000',
  '#808000',
  '#008000',
  '#800080',
  '#008080',
  '#000080',
  '#FFA500',
  '#A52A2A',
];

const wrapListItem = (nodeType, options) => {
  const listCommand = wrapInList(nodeType, options.attrs);

  const command = (state, dispatch, view) => {
    if (!dispatch) return listCommand(state);

    const { direction } = state.selection.$from.parent.attrs;

    return listCommand(
      state,
      tr => {
        if (direction) {
          const { $from } = tr.selection;
          for (let d = $from.depth; d >= 0; d -= 1) {
            const node = $from.node(d);
            if (node.type === nodeType) {
              tr.setNodeMarkup($from.before(d), null, {
                ...node.attrs,
                dir: direction,
              });
              break;
            }
          }
        }
        dispatch(tr);
      },
      view
    );
  };

  return cmdItem(command, options);
};

const imageUploadItem = (nodeType, onImageUpload, className) =>
  new MenuItem({
    title: 'Upload image',
    icon: createIcon(icons.image),
    class: className,
    enable() {
      return true;
    },
    run() {
      onImageUpload();
      return true;
    },
  });

const headerItem = (nodeType, options) => {
  const { level = 1 } = options;
  return new MenuItem({
    title: `Heading ${level}`,
    icon: options.icon,
    class: options.class,
    active(state) {
      return blockTypeIsActive(state, nodeType, { level });
    },
    enable() {
      return true;
    },
    run(state, dispatch, view) {
      if (blockTypeIsActive(state, nodeType, { level })) {
        toggleBlockType(nodeType, { level })(state, dispatch);
        return true;
      }

      toggleBlockType(nodeType, { level })(view.state, view.dispatch);
      view.focus();

      return false;
    },
  });
};

const linkItem = (markType, className) =>
  new MenuItem({
    title: 'Add or remove link',
    icon: createIcon(icons.link),
    class: className,
    active(state) {
      return markActive(state, markType);
    },
    enable(state) {
      return !state.selection.empty;
    },
    run(state, dispatch, view) {
      if (markActive(state, markType)) {
        toggleMark(markType)(state, dispatch);
        return true;
      }
      openPrompt({
        title: 'Create a link',
        fields: {
          href: new TextField({
            label: 'https://example.com',
            class: 'small',
            required: true,
          }),
        },
        callback(attrs) {
          toggleMark(markType, attrs)(view.state, view.dispatch);
          view.focus();
        },
      });
      return false;
    },
  });

const SVG_NS = 'http://www.w3.org/2000/svg';

const createDropdownIcon = (viewBox = '0 0 24 24') => {
  const wrapper = document.createElement('div');
  wrapper.className = 'ProseMirror-icon';

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', viewBox);
  svg.setAttribute('width', '13');
  svg.setAttribute('height', '13');
  svg.setAttribute('fill', '#6B7280');
  wrapper.appendChild(svg);

  return { wrapper, svg };
};

const addSvgPath = (svg, d, attrs = {}) => {
  const node = document.createElementNS(SVG_NS, 'path');
  node.setAttribute('fill', 'currentColor');
  node.setAttribute('d', d);
  Object.entries(attrs).forEach(([key, value]) =>
    node.setAttribute(key, value)
  );
  svg.appendChild(node);
};

const minusIcon = () => {
  const { wrapper, svg } = createDropdownIcon('0 0 13 2');
  addSvgPath(svg, 'M0 1.5V0H13V1.5H0Z');
  return wrapper;
};

const plusIcon = () => {
  const { wrapper, svg } = createDropdownIcon('0 0 13 13');
  addSvgPath(
    svg,
    'M5.75 7.25H0V5.75H5.75V0H7.25V5.75H13V7.25H7.25V13H5.75V7.25Z'
  );
  return wrapper;
};

const fontFamilyItem = (markType, family) => {
  return new MenuItem({
    title: `Set font family to ${family}`,
    label: family,
    render() {
      const wrapper = document.createElement('div');
      wrapper.style.width = '100%'; // Make the wrapper take up 100% of the width
      wrapper.style.display = 'flex';
      wrapper.style.justifyContent = 'flex-start'; // Align text to the left
      wrapper.style.padding = '4px';

      const label = document.createElement('span');
      label.textContent = family;
      label.style.fontFamily = family; // Apply the font family to the label
      label.style.cursor = 'pointer';

      wrapper.appendChild(label);
      return wrapper;
    },
    run: applyMark(markType, 'family', family),
    active(state) {
      const { from, to } = state.selection;
      let isActive = false;
      state.doc.nodesBetween(from, to, node => {
        if (node.marks) {
          node.marks.forEach(mark => {
            if (mark.type === markType && mark.attrs.family === family) {
              isActive = true;
            }
          });
        }
      });
      return isActive;
    },
  });
};

const getActiveMarkAttr = (state, markType, attrKey) => {
  const { from, to, empty, $from } = state.selection;

  if (empty) {
    const marks = state.tr.storedMarks || $from.marks();
    const mark = markType.isInSet(marks);
    return mark ? mark.attrs[attrKey] : null;
  }

  let value = null;
  state.doc.nodesBetween(from, to, node => {
    node.marks.forEach(mark => {
      if (mark.type === markType && mark.attrs[attrKey]) {
        value = mark.attrs[attrKey];
      }
    });
  });
  return value;
};

const getActiveAlignment = state => {
  const { $from } = state.selection;
  const align = $from.parent.attrs.align;
  return ALIGNMENTS.includes(align) ? align : ALIGNMENTS[0];
};

class LabelledDropdown extends Dropdown {
  constructor(content, options, renderLabel) {
    super(content, options);
    this.renderLabel = renderLabel;
  }

  render(view) {
    const { dom, update } = super.render(view);
    const label = dom.querySelector('.ProseMirror-menu-dropdown');

    const updateLabel = state => {
      this.renderLabel(label, state);
    };
    updateLabel(view.state);

    let isOpen = false;

    const closeOnClickOutside = event => {
      if (label.contains(event.target)) return; // Clicking the toggle — handled by toggle logic
      isOpen = false;
      document.removeEventListener('mousedown', closeOnClickOutside, true);
      // Dispatch a synthetic mousedown on the label so the parent's
      // toggle logic runs (open→close, sets open=null, removes its listener).
      label.dispatchEvent(
        new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
    };

    // Track open/close state via the label's mousedown (capture = before parent's handler).
    label.addEventListener(
      'mousedown',
      e => {
        if (!e.isTrusted) return; // ignore our own synthetic events
        if (isOpen) {
          isOpen = false;
          document.removeEventListener('mousedown', closeOnClickOutside, true);
        } else {
          isOpen = true;
          // Defer so this opening mousedown doesn't immediately trigger closeOnClickOutside.
          setTimeout(() => {
            document.addEventListener('mousedown', closeOnClickOutside, true);
          }, 0);
        }
      },
      true
    );

    return {
      dom,
      update(state) {
        updateLabel(state);
        return update(state);
      },
    };
  }

  expand(dom, items) {
    const result = super.expand(dom, items);
    const gap = 4;
    const anchor = dom.querySelector('.ProseMirror-menu-dropdown');
    const dropdownMenu = dom.querySelector('.ProseMirror-menu-dropdown-menu');

    Object.assign(dropdownMenu.style, {
      zIndex: '30',
      height: 'fit-content',
      width: 'fit-content',
    });

    const updatePosition = () => {
      Object.assign(
        dropdownMenu.style,
        getFixedDialogStyle(anchor, {
          width: dropdownMenu.offsetWidth,
          height: dropdownMenu.offsetHeight,
          gap,
          align: 'left',
          preferredPlacement: 'top',
        })
      );
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);

    return {
      ...result,
      close: () => {
        const closed = result.close();
        if (closed) {
          window.removeEventListener('scroll', updatePosition, true);
        }
        return closed;
      },
    };
  }
}

const fontFamilyDropdown = markType => {
  const FONT_FAMILIES = [
    'Arial',
    'Courier New',
    'Georgia',
    'Times New Roman',
    'Verdana',
    'Helvetica',
    'Helvetica Neue',
    'San Francisco',
    'Monaco',
    'Lucida Grande',
    'Geneva',
    'Apple Chancery',
    'Brush Script MT',
    'Palatino',
    'Didot',
    'Futura',
    'Gill Sans',
  ];
  const items = FONT_FAMILIES.map(family => fontFamilyItem(markType, family));
  return new LabelledDropdown(
    items,
    {
      label: FONT_FAMILIES[0],
      title: 'Font',
      class: 'menu-item-fontFamily-option',
    },
    (label, state) => {
      label.textContent =
        getActiveMarkAttr(state, markType, 'family') || FONT_FAMILIES[0];
    }
  );
};

const FONT_SIZE_MIN = 8;
const FONT_SIZE_MAX = 96;
const FONT_SIZE_STEP = 1;
const FONT_SIZE_DEFAULT = 16;

const fontSizeControl = markType => ({
  render(view) {
    const wrapper = document.createElement('div');
    wrapper.className = 'ProseMirror-menu-fontsize menu-item-fontSize-option';

    const input = document.createElement('input');
    input.type = 'number';
    input.className = 'ProseMirror-menu-fontsize-input';
    input.min = FONT_SIZE_MIN;
    input.max = FONT_SIZE_MAX;
    input.title = 'Font size';

    const getCurrentSize = state =>
      parseInt(getActiveMarkAttr(state, markType, 'size'), 10) ||
      FONT_SIZE_DEFAULT;

    const setSize = size => {
      const clamped = Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, size));
      applyMark(markType, 'size', clamped)(view.state, view.dispatch, view);
      input.value = clamped;
    };

    const decreaseButton = document.createElement('button');
    decreaseButton.type = 'button';
    decreaseButton.className = 'ProseMirror-menu-fontsize-btn';
    decreaseButton.title = 'Decrease font size';
    decreaseButton.appendChild(minusIcon());
    decreaseButton.addEventListener('mousedown', e => {
      e.preventDefault();
      setSize(getCurrentSize(view.state) - FONT_SIZE_STEP);
    });

    const increaseButton = document.createElement('button');
    increaseButton.type = 'button';
    increaseButton.className = 'ProseMirror-menu-fontsize-btn';
    increaseButton.title = 'Increase font size';
    increaseButton.appendChild(plusIcon());
    increaseButton.addEventListener('mousedown', e => {
      e.preventDefault();
      setSize(getCurrentSize(view.state) + FONT_SIZE_STEP);
    });

    const commitInputValue = () => {
      const value = parseInt(input.value, 10);
      if (!Number.isNaN(value)) {
        setSize(value);
      } else {
        input.value = getCurrentSize(view.state);
      }
      view.focus();
    };
    input.addEventListener('change', commitInputValue);
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitInputValue();
      }
    });

    wrapper.appendChild(decreaseButton);
    wrapper.appendChild(input);
    wrapper.appendChild(increaseButton);

    const update = state => {
      if (document.activeElement !== input) {
        input.value = getCurrentSize(state);
      }
      return true;
    };
    update(view.state);

    return { dom: wrapper, update };
  },
});

const colorPickerDropdown = (markType, colors) => {
  const items = colors.map(color => {
    return new MenuItem({
      title: `Set text color to ${color}`,
      label: '',
      render() {
        const wrapper = document.createElement('div');
        wrapper.style.width = '100%'; // Make the wrapper take up 100% of the width
        wrapper.style.display = 'flex';
        wrapper.style.justifyContent = 'center'; // Center the button inside the div
        wrapper.style.padding = '3.5px';

        const button = document.createElement('button');
        button.type = 'button';
        button.style.backgroundColor = color;
        button.style.width = '17px';
        button.style.height = '17px';
        button.style.borderRadius = '4px';
        button.style.cursor = 'pointer';
        button.title = `Set text color to ${color}`;

        wrapper.appendChild(button); // Wrap the button inside the div
        return wrapper;
      },
      run: applyMark(markType, 'color', color),
      active(state) {
        const { from, to } = state.selection;
        let isActive = false;
        state.doc.nodesBetween(from, to, node => {
          if (node.marks) {
            node.marks.forEach(mark => {
              if (mark.type === markType && mark.attrs.color === color) {
                isActive = true;
              }
            });
          }
        });
        return isActive;
      },
    });
  });

  return new LabelledDropdown(
    items,
    {
      label: '',
      title: 'Text Color',
      class: 'text-color-menu menu-item-textColor-option',
    },
    (label, state) => {
      const color = getActiveMarkAttr(state, markType, 'color') || colors[0];

      let swatch = label.querySelector('.ProseMirror-menu-dropdown-swatch');
      if (!swatch) {
        label.textContent = '';
        swatch = document.createElement('span');
        swatch.className = 'ProseMirror-menu-dropdown-swatch';
        label.appendChild(swatch);
      }
      swatch.style.backgroundColor = color;
    }
  );
};

const alignmentDropdown = nodeType => {
  const items = ALIGNMENTS.map(alignment =>
    alignmentItem(nodeType, alignment, ALIGNMENT_CLASS_NAMES[alignment])
  );

  return new LabelledDropdown(
    items,
    {
      label: '',
      title: 'Alignment',
      class: 'alignment-menu menu-item-alignment-option',
    },
    (label, state) => {
      const alignment = getActiveAlignment(state);
      label.textContent = '';
      const icon = document.createElement('span');
      icon.className = 'ProseMirror-icon';
      icon.appendChild(createIcon(icons[alignment]).dom);
      label.appendChild(icon);
    }
  );
};

const buildMenuOptions = (
  schema,
  {
    enabledMenuOptions = [
      'strong',
      'em',
      'code',
      'link',
      'undo',
      'redo',
      'bulletList',
      'orderedList',
      'alignment',
      'strike',
      'fontSize',
      'fontFamily',
      'textColor',
    ],
    onImageUpload = () => {},
  }
) => {
  const availableMenuOptions = {
    strong: markItem(schema.marks.strong, {
      title: 'Toggle strong style',
      icon: createIcon(icons.strong),
      class: 'menu-item-strong-option',
    }),
    em: markItem(schema.marks.em, {
      title: 'Toggle emphasis',
      icon: createIcon(icons.em),
      class: 'menu-item-em-option',
    }),
    code: markItem(schema.marks.code, {
      title: 'Toggle code font',
      icon: createIcon(icons.code),
      class: 'menu-item-code-option',
    }),
    link: linkItem(schema.marks.link, 'menu-item-link-option'),
    bulletList: wrapListItem(schema.nodes.bullet_list, {
      title: 'Wrap in bullet list',
      icon: createIcon(icons.bulletList),
      class: 'menu-item-bulletList-option',
    }),
    orderedList: wrapListItem(schema.nodes.ordered_list, {
      title: 'Wrap in ordered list',
      icon: createIcon(icons.orderedList),
      class: 'menu-item-orderedList-option',
    }),
    undo: new MenuItem({
      title: 'Undo last change',
      run: undo,
      enable: state => undo(state),
      icon: createIcon(icons.undo),
      class: 'menu-item-undo-option',
    }),
    redo: new MenuItem({
      title: 'Redo last undone change',
      run: redo,
      enable: state => redo(state),
      icon: createIcon(icons.redo),
      class: 'menu-item-redo-option',
    }),
    h1: headerItem(schema.nodes.heading, {
      level: 1,
      title: 'Toggle code font',
      icon: createIcon(icons.h1),
      class: 'menu-item-h1-option',
    }),
    h2: headerItem(schema.nodes.heading, {
      level: 2,
      title: 'Toggle code font',
      icon: createIcon(icons.h2),
      class: 'menu-item-h2-option',
    }),
    h3: headerItem(schema.nodes.heading, {
      level: 3,
      title: 'Toggle code font',
      icon: createIcon(icons.h3),
      class: 'menu-item-h3-option',
    }),
    imageUpload: imageUploadItem(
      schema.nodes.image,
      onImageUpload,
      'menu-item-imageUpload-option'
    ),
    alignment: alignmentDropdown(schema.nodes.paragraph),
    fontSize: fontSizeControl(schema.marks.fontSize),
    fontFamily: fontFamilyDropdown(schema.marks.fontFamily),
    textColor: colorPickerDropdown(schema.marks.textColor, TEXT_COLORS),
  };

  const alwaysEnabledMenuOptions = [
    'alignment',
    'fontSize',
    'fontFamily',
    'textColor',
  ];

  alwaysEnabledMenuOptions.forEach(menuOption => {
    if (!enabledMenuOptions.includes(menuOption))
      enabledMenuOptions.push(menuOption);
  });

  return [
    enabledMenuOptions
      .filter(menuOptionKey => !!availableMenuOptions[menuOptionKey])
      .map(menuOptionKey => availableMenuOptions[menuOptionKey]),
  ];
};

export default buildMenuOptions;
